<template>
    <div class="max-w-3xl mx-auto px-4 py-4 font-sans text-gray-800">

        <div class="mb-8">
            <NuxtLink to="/resources" class="text-secondary hover:text-secondary/80 flex items-center mb-4">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                </svg>
                Resources
            </NuxtLink>
        </div>

        <img src="/images/resources/first-30-days-home-insurance-inventory-tips.png" alt="New Home Insurance Inventory"
            class="w-full h-64 object-cover rounded-xl shadow-md mb-8">

        <h1 class="text-4xl font-extrabold tracking-tight text-gray-900 mb-8 leading-tight">
            Just Bought a Home? Here’s Why Your First 30 Days are the Most Critical for Insurance
        </h1>

        <p class="text-lg leading-relaxed mb-6">Congratulations on the new home. You’ve navigated the inspections,
            signed the mountain of paperwork, and finally
            have the keys. But amid the excitement of unboxing and paint swatches, there is a narrow 30-day window that
            will
            determine your financial resilience for years to come.</p>

        <p class="text-lg leading-relaxed mb-6">Most homeowners view insurance as a "set it and forget it" checkbox.
            This is a dangerous mistake. The first 30
            days of homeownership are the only time you will ever have a "clean slate" to document the base state of
            your
            property and possessions.</p>

        <p class="text-lg leading-relaxed mb-6">If you don't capture this moment now, you are leaving your future self
            at the mercy of an insurance adjuster’s
            skepticism.</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-12 mb-4">The "Pre-Existing Condition" Trap</h2>
        <p class="text-lg leading-relaxed mb-6">When you file a claim three years from now for a burst pipe or storm
            damage, the insurance company’s first line
            of defense is often to claim the damage was "pre-existing" or due to "lack of maintenance."</p>

        <p class="text-lg leading-relaxed mb-6">Without a forensic record of your home’s condition at the time of
            purchase, it becomes your word against their
            engineering report. By documenting your home in the first 30 days—while rooms are relatively empty or newly
            furnished—you create a "Day Zero" baseline.</p>

        <p class="text-lg leading-relaxed mb-6">This baseline is the ultimate leverage. It proves exactly what the
            floors, walls, and ceilings looked like
            before any incident occurred, making it nearly impossible for an adjuster to argue that your claim is a
            result
            of long-term neglect.</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-12 mb-4">Documenting the "Inventory Surge"</h2>
        <p class="text-lg leading-relaxed mb-6">The first month in a new home usually involves significant spending. New
            appliances, furniture, and electronics
            are arriving daily. In the chaos of moving, receipts are lost and boxes are thrown away.</p>

        <p class="text-lg leading-relaxed mb-6">This is the most critical time to execute a private home inventory.</p>

        <p class="text-lg leading-relaxed mb-6">Insurance adjusters rely on the fact that most people cannot prove the
            specific model of their new HVAC unit or
            the high-end finish of their kitchen cabinetry. If you wait until after a disaster to list these items, you
            are
            performing a "memory test" you are guaranteed to lose. Documenting these high-value assets now—with serial
            numbers and high-resolution photos—ensures you are paid for the replacement cost, not a depreciated
            "ballpark"
            value.</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-12 mb-4">The Privacy Risk: Who Has the Map to Your New Home?
        </h2>
        <p class="text-lg leading-relaxed mb-6">As you begin to document your new life, you face a modern dilemma.
            Traditional home inventory apps ask you to
            upload photos of every room, every piece of jewelry, and every expensive electronic to their cloud servers.
        </p>

        <p class="text-lg leading-relaxed mb-6">For a new homeowner, this is a massive security nightmare. You are
            essentially handing a digital blueprint of
            your home’s contents and security vulnerabilities to a third-party company. If their database is breached,
            your
            most intimate home details are on the dark web.</p>

        <p class="text-lg leading-relaxed mb-6">You need to protect your claim value without creating a security hole in
            your new home. This is where the
            "Local-Only" standard becomes essential.</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-12 mb-4">The Solution: Audit-Ready, Privacy-First</h2>
        <p class="text-lg leading-relaxed mb-6">This is why we built ClaimReady. We believe you shouldn't have to choose
            between financial security and digital
            privacy.</p>

        <p class="text-lg leading-relaxed mb-6">ClaimReady is a "Zero-Knowledge" utility. It allows you to document your
            entire new home and all its contents in
            under 20 minutes. The magic? Your data never leaves your device. The photos of your new safe, your
            electronics,
            and your family heirlooms are processed locally in your browser. You don’t need an account, and we never see
            your files. When you're finished, the app generates a professional, high-resolution PDF report designed to
            meet
            the evidentiary standards of insurance adjusters.</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-12 mb-4">Your 30-Day Checklist</h2>
        <p class="text-lg leading-relaxed mb-6">Don't let the "honeymoon phase" of your new home leave you vulnerable.
        </p>

        <ul class="list-disc pl-6 mb-6 space-y-2">
            <li><strong>Walk the Perimeter:</strong> Take high-res photos of every wall, ceiling, and floor before they
                are obscured by decor.</li>
            <li><strong>Log the Systems:</strong> Snap photos of the water heater, HVAC model plates, and electrical
                panels.</li>
            <li><strong>Inventory the Assets:</strong> Document new purchases as they arrive.</li>
            <li><strong>Secure the Proof:</strong> Use a local-only tool to wrap this data into a forensic report.</li>
        </ul>

        <h2 class="text-2xl font-bold text-gray-900 mt-12 mb-4">Conclusion</h2>
        <p class="text-lg leading-relaxed mb-6">Protect your new investment today. Use ClaimReady to build your "Day
            Zero" forensic inventory in 20 minutes for
            a one-time fee of $6.99. No subscriptions, no cloud risks—just audit-ready proof.</p>

        <UCard class="font-semibold text-lg">Don't let the
            "honeymoon phase" leave you vulnerable. Build your baseline inventory
            today.</UCard>

        <div class="mt-8 flex justify-center">
            <UButton label="Get Started" to="/" size="xl" />
        </div>
    </div>
</template>

<script setup lang="ts">
definePageMeta({
    layout: 'default'
})
useHead({
    title: 'Just Bought a Home? The First 30 Days are Critical for Insurance',
    meta: [
        { name: 'description', content: 'The first 30 days of homeownership are your only chance to document a "base state" for insurance. Learn how to create a private, forensic inventory to protect your payout.' },
        { name: 'keywords', content: 'home insurance claim tips, new homeowner inventory, private home inventory, documenting valuables for insurance, zero-knowledge apps' },
        // Open Graph
        { property: 'og:title', content: 'Just Bought a Home? The First 30 Days are Critical for Insurance' },
        { property: 'og:description', content: 'Don’t fall into the "pre-existing condition" trap. Document your new home now with a private, audit-ready inventory.' },
        { property: 'og:type', content: 'article' },
        { property: 'og:url', content: 'https://claimready.app/resources/first-30-days-home-insurance-inventory-tips' },
        { property: 'og:image', content: 'https://claimready.app/images/resources/first-30-days-home-insurance-inventory-tips.png' },
        // Twitter
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: 'Critical Insurance Tips for New Homeowners' },
        { name: 'twitter:description', content: 'Your first 30 days determine your insurance resilience. Create a forensic inventory without uploading your data to the cloud.' },
        { name: 'twitter:image', content: 'https://claimready.app/images/resources/first-30-days-home-insurance-inventory-tips.png' }
    ],
    link: [
        { rel: 'canonical', href: 'https://claimready.app/resources/first-30-days-home-insurance-inventory-tips' }
    ]
})
</script>